import { useCallback, useEffect, useState } from "react";

import { useApi } from "@platform/hooks/useApi";

import type { NotificationPreferences } from "../types";

interface UseNotificationPreferencesReturn {
  preferences: NotificationPreferences | null;
  loading: boolean;
  error: string | null;
  updatePreferences: (
    next: NotificationPreferences,
  ) => Promise<NotificationPreferences | null>;
  setLocalPreferences: (next: NotificationPreferences) => void;
}

/** Default preferences used while the API response is in-flight. */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  emailEnabled: true,
  pushEnabled: false,
  resourceEvents: true,
  billingEvents: true,
  securityEvents: true,
};

/**
 * useNotificationPreferences — fetches the user's notification preferences and
 * exposes an `updatePreferences` mutation that PATCHes the API.
 *
 * `setLocalPreferences` allows callers to keep a local editable copy in sync
 * without an extra round-trip; `updatePreferences` persists to the server and
 * updates the shared state on success.
 */
export function useNotificationPreferences(): UseNotificationPreferencesReturn {
  const api = useApi();
  const [preferences, setPreferences] =
    useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<NotificationPreferences>(
        "/api/v1/notifications/preferences",
        {
          errorMessage: "Failed to load notification preferences",
          showLoading: false,
        },
      );
      setPreferences(result ?? DEFAULT_NOTIFICATION_PREFERENCES);
    } catch {
      setError("Failed to load notification preferences.");
    } finally {
      setLoading(false);
    }
    // `api` excluded — see useProjectResources note in useResources.ts.
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const updatePreferences = useCallback(
    async (
      next: NotificationPreferences,
    ): Promise<NotificationPreferences | null> => {
      try {
        const result = await api.patch<
          NotificationPreferences,
          NotificationPreferences
        >("/api/v1/notifications/preferences", next, {
          errorMessage: "Failed to update notification preferences",
          showLoading: false,
        });
        if (result) {
          setPreferences(result);
        }
        return result;
      } catch {
        return null;
      }
    },
    // `api` excluded — see note above. api.patch is stable.
    [],
  );

  const setLocalPreferences = useCallback((next: NotificationPreferences) => {
    setPreferences(next);
  }, []);

  return {
    preferences,
    loading,
    error,
    updatePreferences,
    setLocalPreferences,
  };
}
